import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AdminContext";
import {
  useGetResourceByIdQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  useUploadMediaMutation,
  useGetFormsQuery,
} from "../api/businessApi";
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import TemplatePicker from "../components/TemplatePicker";


// SunEditor Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  return (
    <SunEditor
      setContents={value || ''}
      onChange={onChange}
      placeholder={placeholder || "Start writing your content here..."}
      setOptions={{
        height: '550px',
        buttonList: [
          ['undo', 'redo'],
          ['font', 'fontSize'], // Removed formatBlock to avoid error
          ['paragraphStyle', 'blockquote'],
          ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
          ['fontColor', 'hiliteColor', 'textStyle'],
          ['removeFormat'],
          '/', // Line break
          ['outdent', 'indent'],
          ['align', 'horizontalRule', 'list', 'lineHeight'],
          ['table', 'link', 'image', 'video'],
          ['fullScreen', 'showBlocks', 'codeView', 'preview']
        ],
        defaultStyle: 'font-family: Arial; font-size: 16px;',
        mode: 'classic',
        katex: false,
        videoTagStyle: 'width: 100%; max-width: 100%;',
        imageUploadUrl: false, // Disable server upload
        videoUploadUrl: false, // Disable server upload
        imageFileInput: {
          accept: 'image/*',
          multiple: false
        },
        videoFileInput: {
          accept: 'video/*',
          multiple: false
        },
        templates: [
          {
            name: 'Template-1',
            html: '<p>Start typing your content here...</p>'
          },
          {
            name: 'Template-2',
            html: '<h2>Article Title</h2><p>Your article content goes here...</p>'
          }
        ],
        resizingBar: true,
        showPathLabel: false,
        font: [
          'Arial', 'Comic Sans MS', 'Courier New', 'Georgia', 'Helvetica', 'Impact', 'Times New Roman', 'Verdana'
        ],
        fontSize: [
          8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72
        ],
        colorList: [
          '#ff0000', '#ff4500', '#ff8c00', '#ffd700', '#008000', '#006400', '#00ff00', '#00ffff', '#00ffff', '#0000ff', '#000080', '#0000cd', '#4169e1', '#4b0082', '#800080', '#808080', '#f0e68c', '#a52a2a', '#d2691e', '#ff7f50', '#696969'
        ],
        formats: [
          'p', 'div', 'span', 'br', 'strong', 'u', 'em', 's', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
          'a', 'img', 'video', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'pre', 'code'
        ],
        historyStackDelayTime: 100,
        attributesWhitelist: {
          all: true
        },
        tag: {
          'strong': 'b',
          'em': 'i',
          's': 'del'
        }
      }}
      onImageUploadBefore={(files, info, uploadHandler) => {
        // Handle image upload locally
        const file = files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target.result;
            uploadHandler({
              result: [{
                url: dataUrl,
                name: file.name,
                size: file.size
              }]
            });
          };
          reader.readAsDataURL(file);
        } else {
          uploadHandler();
        }
        return false;
      }}
      onVideoUploadBefore={(files, info, uploadHandler) => {
        // Handle video upload locally
        const file = files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target.result;
            uploadHandler({
              result: [{
                url: dataUrl,
                name: file.name,
                size: file.size
              }]
            });
          };
          reader.readAsDataURL(file);
        } else {
          uploadHandler();
        }
        return false;
      }}
      onPaste={(e, cleanData, maxCharCount) => {
        console.log('Content pasted:', cleanData);
      }}
      onCut={(e, cleanData) => {
        console.log('Content cut:', cleanData);
      }}
      onCopy={(e, cleanData) => {
        console.log('Content copied:', cleanData);
      }}
      onDrop={(e) => {
        console.log('Files dropped:', e);
      }}
      onFocus={(e) => {
        console.log('Editor focused');
      }}
      onBlur={(e) => {
        console.log('Editor blurred');
      }}
    />
  );
};

const BlogEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    newsletter_id: '',
    title: '',
    excerpt: '',
    content: '',
    author: user?.name || 'Admin',
    read_time: '5 min read',
    featured: false,
    category: 'Business',
    publish_date: new Date().toISOString().split('T')[0],
    status: 'draft',
    content_type: 'blog',
    video_url: '',
    file_url: '',
    file_size: 0,
    file_type: '',
    tags: [],
    featured_image: '',
    images: [],
    form_id: null,
    external_form_url: '',
  });
  const [fileUploading, setFileUploading] = useState(false);
  const docFileInputRef = useRef(null);
  const [uploadMedia] = useUploadMediaMutation();
  const [tagInput, setTagInput] = useState('');
  const { data: availableForms = [] } = useGetFormsQuery();

  const [errors, setErrors] = useState({});
  const [editorMode, setEditorMode] = useState('rich'); // 'rich', 'html', 'preview'
  const [isLargeContent, setIsLargeContent] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(!isEditing);

  const LARGE_CONTENT_THRESHOLD = 100000; // 100KB

  const { data: newsletterData, isLoading: isLoadingNewsletter, isError: isNewsletterError } = useGetResourceByIdQuery(id, { skip: !isEditing || !id });
  const [createResource, { data: createData, isSuccess: isCreateSuccess, isLoading: isCreateLoading, isError: isCreateError, error: createError }] = useCreateResourceMutation();
  const [updateResource, { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading, isError: isUpdateError, error: updateError }] = useUpdateResourceMutation();

  // Load newsletter data when editing - use direct API fetch by ID instead of paginated list
  useEffect(() => {
    if (isEditing && newsletterData) {
      const newsletter = newsletterData;
      const contentLength = (newsletter.content || '').length;
      const large = contentLength > LARGE_CONTENT_THRESHOLD;
      setIsLargeContent(large);
      // Auto-switch editor mode for special content:
      // - Large content → preview (to avoid freezing the editor)
      // - Inline-styled HTML (e.g. from templates) → HTML source (editable, preserves styles)
      // SunEditor strips inline styles, so styled content must never load in rich mode
      const hasInlineStyles = (newsletter.content || '').includes('style="');
      if (hasInlineStyles) {
        setEditorMode('html');
      } else if (large) {
        setEditorMode('preview');
      }
      setFormData({
        newsletter_id: newsletter.newsletter_id || '',
        title: newsletter.title || '',
        excerpt: newsletter.excerpt || '',
        content: newsletter.content || '',
        author: newsletter.author || user?.name || 'Admin',
        read_time: newsletter.read_time || '5 min read',
        featured: newsletter.featured || false,
        category: newsletter.category || 'Business',
        publish_date: newsletter.publish_date?.split?.('T')?.[0] || new Date().toISOString().split('T')[0],
        status: newsletter.status || 'draft',
        content_type: newsletter.content_type || 'blog',
        video_url: newsletter.video_url || '',
        file_url: newsletter.file_url || '',
        file_size: newsletter.file_size || 0,
        file_type: newsletter.file_type || '',
        tags: newsletter.tags || [],
        featured_image: newsletter.featured_image || '',
        images: newsletter.images || [],
        form_id: newsletter.form_id || null,
        external_form_url: newsletter.external_form_url || '',
      });
    }
  }, [isEditing, newsletterData, user?.name]);

  // Handle create success
  useEffect(() => {
    if (isCreateSuccess) {
      console.log('Newsletter created successfully');
      navigate('/admin/dashboard?tab=newsletters');
    }
  }, [isCreateSuccess, navigate]);

  // Handle update success
  useEffect(() => {
    if (isUpdateSuccess) {
      console.log('Newsletter updated successfully');
      navigate('/admin/dashboard?tab=newsletters');
    }
  }, [isUpdateSuccess, navigate]);

  // Handle create error
  useEffect(() => {
    if (isCreateError) {
      console.error('Create error:', createError);
      const errorMessage = createError?.data?.error ||
        createError?.data?.message ||
        createError?.message ||
        'Failed to create newsletter';
      setErrors({ submit: errorMessage });
    }
  }, [isCreateError, createError]);

  useEffect(() => {
    if (!isCreateLoading) {
      console.log('Create result:', createData, createError);
    }
  }, [isCreateLoading, createData, createError]);
  // Handle update error
  useEffect(() => {
    if (isUpdateError) {
      console.error('Update error:', updateError);
      const errorMessage = updateError?.data?.error ||
        updateError?.data?.message ||
        updateError?.message ||
        'Failed to update newsletter';
      setErrors({ submit: errorMessage });
    }
  }, [isUpdateError, updateError]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = [];

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target.result;
          newImages.push(base64String);

          // Add to form data when all files are processed
          if (newImages.length === files.length) {
            setFormData(prev => ({
              ...prev,
              images: [...prev.images, ...newImages]
            }));
          }
        };
        reader.readAsDataURL(file);
      }
    });

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formData.publish_date) {
      newErrors.publish_date = 'Publish date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const newsletterData = {
        ...formData,
        // Generate newsletter_id from title and date if not provided
        newsletter_id: formData.newsletter_id ||
          `${formData.title.toLowerCase().replace(/\s+/g, '-')}-${formData.publish_date}`
      };

      if (isEditing) {
        updateResource({ id, ...newsletterData }).unwrap();
      } else {
        createResource(newsletterData);
      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      // The error will be handled by the useEffect hooks, but we catch it to prevent uncaught rejections
    }
  };
  const handleCancel = () => {
    navigate('/admin/dashboard?tab=newsletters');
  };

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      content: template.html || '',
      content_type: template.content_type || prev.content_type,
    }));
    // Use preview mode for templates with styled HTML, rich editor for blank
    if (template.html) {
      setEditorMode('preview');
    } else {
      setEditorMode('rich');
    }
    setShowTemplatePicker(false);
  };

  // Show template picker for new resources
  if (showTemplatePicker && !isEditing) {
    return (
      <TemplatePicker
        onSelect={handleTemplateSelect}
        onCancel={() => navigate('/admin/dashboard?tab=newsletters')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Overlay */}
      {isEditing && isLoadingNewsletter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#135879] mb-4"></div>
            <p className="text-gray-700">Loading newsletter data...</p>
          </div>
        </div>
      )}

      {/* Newsletter not found */}
      {isEditing && isNewsletterError && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 font-medium">Newsletter not found with ID: {id}</p>
            <button
              onClick={() => navigate('/admin/dashboard?tab=newsletters')}
              className="mt-4 px-4 py-2 bg-[#135879] text-white rounded-lg hover:bg-[#0f4568]"
            >
              Back to Newsletters
            </button>
          </div>
        </div>
      )}

      {/* Header and Form - hide when newsletter not found */}
      {!(isEditing && isNewsletterError) && (
      <>
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? `Edit ${formData.content_type.charAt(0).toUpperCase() + formData.content_type.slice(1)}` : 'Create New Resource'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isEditing ? 'Update your content' : 'Create and publish a new resource (newsletter, blog, video, guide, or announcement)'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isCreateLoading || isUpdateLoading}
                className="px-4 py-2 bg-[#135879] text-white rounded-lg hover:bg-[#0f4568] disabled:opacity-50"
              >
                {(isCreateLoading || isUpdateLoading) ? 'Saving...' : (isEditing ? 'Update' : 'Publish')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type *
                </label>
                <select
                  name="content_type"
                  value={formData.content_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  <option value="newsletter">Newsletter</option>
                  <option value="blog">Blog</option>
                  <option value="video">Video</option>
                  <option value="guide">Guide</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879] ${errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter title"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  <option value="Business">Business</option>
                  <option value="Technology">Technology</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Finance">Finance</option>
                  <option value="Networking">Networking</option>
                  <option value="Events">Events</option>
                  <option value="Monthly Newsletter">Monthly Newsletter</option>
                  <option value="Community">Community</option>
                  <option value="Education">Education</option>
                  <option value="Business Templates">Business Templates</option>
                  <option value="Presentations & Charters">Presentations &amp; Charters</option>
                  <option value="Forms & Documents">Forms &amp; Documents</option>
                </select>
              </div>

              {/* Publish Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publish Date *
                </label>
                <input
                  type="date"
                  name="publish_date"
                  value={formData.publish_date}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879] ${errors.publish_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {errors.publish_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.publish_date}</p>
                )}
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  placeholder="Author name"
                />
              </div>

              {/* Featured Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Image URL
                </label>
                <input
                  type="text"
                  name="featured_image"
                  value={formData.featured_image}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  placeholder="https://... or upload via Media Library"
                />
                {formData.featured_image && (
                  <img src={formData.featured_image} alt="Preview" className="mt-2 h-20 rounded border object-cover" />
                )}
              </div>
            </div>

            {/* Video URL (only for video type) */}
            {formData.content_type === 'video' && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL *
                </label>
                <input
                  type="text"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  placeholder="YouTube or Vimeo URL (e.g., https://www.youtube.com/watch?v=...)"
                />
                <p className="text-xs text-gray-500 mt-1">Paste a YouTube or Vimeo link. It will be embedded automatically.</p>
              </div>
            )}

            {/* File Attachment (for guide type) */}
            {formData.content_type === 'guide' && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Downloadable File (PDF, PPT, DOCX, XLSX, ZIP)
                </label>
                {formData.file_url ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-2xl">
                      {formData.file_type?.includes('pdf') ? '📄' :
                       formData.file_type?.includes('presentation') || formData.file_type?.includes('powerpoint') ? '📊' :
                       formData.file_type?.includes('word') ? '📝' :
                       formData.file_type?.includes('sheet') || formData.file_type?.includes('excel') ? '📈' : '📎'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{formData.file_url.split('/').pop()}</p>
                      <p className="text-xs text-gray-500">
                        {formData.file_type?.split('/').pop().toUpperCase()}
                        {formData.file_size > 0 && ` · ${(formData.file_size / (1024*1024)).toFixed(1)} MB`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, file_url: '', file_size: 0, file_type: '' }))}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => docFileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                      fileUploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-[#135879] hover:bg-gray-50'
                    }`}
                  >
                    {fileUploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#135879] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-gray-600">Click to upload a file</p>
                        <p className="text-xs text-gray-400 mt-1">PDF, PPT, PPTX, DOC, DOCX, XLS, XLSX, ZIP (max 20MB)</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={docFileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.zip"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setFileUploading(true);
                    try {
                      const uploadFormData = new FormData();
                      uploadFormData.append('files', file);
                      const result = await uploadMedia(uploadFormData).unwrap();
                      // API returns { media: [{ file_url, size, mime_type, ... }] }
                      const media = result.media?.[0] || result;
                      setFormData(prev => ({
                        ...prev,
                        file_url: media.file_url || media.url,
                        file_size: media.size || file.size,
                        file_type: media.mime_type || file.type,
                      }));
                    } catch (err) {
                      console.error('File upload error:', err);
                      alert('Upload failed. Please try again.');
                    } finally {
                      setFileUploading(false);
                      if (docFileInputRef.current) docFileInputRef.current.value = '';
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  This file will be available as a downloadable resource on the Guides page.
                </p>
              </div>
            )}

            {/* Form Attachment */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attach a Response Form <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Add a form so readers can express interest or respond directly on the page. Choose a saved form or embed a Google Form.
              </p>

              {/* Toggle: custom vs google */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, external_form_url: '' }))}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${!formData.external_form_url && formData.form_id !== 'google' ? 'bg-[#135879] text-white border-[#135879]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                >
                  Saved Form
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, form_id: null, external_form_url: prev.external_form_url || ' ' }))}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${formData.external_form_url ? 'bg-[#135879] text-white border-[#135879]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                >
                  Google Form (embed URL)
                </button>
                {(formData.form_id || formData.external_form_url?.trim()) && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, form_id: null, external_form_url: '' }))}
                    className="px-3 py-1.5 rounded-lg text-sm border border-red-200 text-red-500 hover:bg-red-50"
                  >
                    Remove Form
                  </button>
                )}
              </div>

              {formData.external_form_url ? (
                <div>
                  <input
                    type="url"
                    value={formData.external_form_url.trim()}
                    onChange={(e) => setFormData(prev => ({ ...prev, external_form_url: e.target.value }))}
                    placeholder="https://docs.google.com/forms/d/.../viewform?embedded=true"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  />
                  <p className="text-xs text-gray-400 mt-1">Paste the Google Form embed URL (File → Embed → copy the src attribute from the iframe code).</p>
                </div>
              ) : (
                <select
                  value={formData.form_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, form_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  <option value="">— No form —</option>
                  {availableForms.map((f) => (
                    <option key={f.id} value={f.id}>{f.title}</option>
                  ))}
                </select>
              )}
              {availableForms.length === 0 && !formData.external_form_url && (
                <p className="text-xs text-gray-400 mt-1">
                  No forms yet. <a href="/admin/forms" className="text-[#135879] underline" target="_blank" rel="noreferrer">Create one in Forms</a> first.
                </p>
              )}
            </div>

            {/* Tags */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.tags || []).map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#135879]/10 text-[#135879]">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== idx) }))}
                      className="ml-1.5 text-[#135879]/60 hover:text-[#135879]"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault();
                      if (!formData.tags.includes(tagInput.trim())) {
                        setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
                      }
                      setTagInput('');
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  placeholder="Type a tag and press Enter"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
                      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
                      setTagInput('');
                    }
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt *
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879] ${errors.excerpt ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Brief description of the newsletter content"
              />
              {errors.excerpt && (
                <p className="mt-1 text-sm text-red-600">{errors.excerpt}</p>
              )}
            </div>
          </div>

          {/* Content Editor */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Content</h2>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  disabled={formData.content.includes('style="')}
                  title={formData.content.includes('style="') ? 'Rich Editor is disabled because this content uses inline styles (from a template). Use HTML Source to edit safely.' : ''}
                  onClick={() => {
                    if (isLargeContent) {
                      if (!window.confirm('This content is very large and may freeze the rich text editor. Continue?')) return;
                    }
                    setEditorMode('rich');
                  }}
                  className={`px-3 py-1.5 text-xs font-medium transition ${
                    formData.content.includes('style="')
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : editorMode === 'rich'
                        ? 'bg-[#135879] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Rich Editor
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('html')}
                  className={`px-3 py-1.5 text-xs font-medium transition border-l border-gray-300 ${
                    editorMode === 'html'
                      ? 'bg-[#135879] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  HTML Source
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('preview')}
                  className={`px-3 py-1.5 text-xs font-medium transition border-l border-gray-300 ${
                    editorMode === 'preview'
                      ? 'bg-[#135879] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Preview
                </button>
              </div>
            </div>

            {formData.content.includes('style="') && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                <strong>Styled content</strong> — This content uses a template with inline styles. Edit text in <strong>HTML Source</strong> mode or view it in <strong>Preview</strong>. The Rich Editor is disabled to protect formatting.
              </div>
            )}

            {isLargeContent && editorMode !== 'rich' && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <strong>Large content detected</strong> ({Math.round(formData.content.length / 1024)}KB). Using {editorMode === 'preview' ? 'Preview' : 'HTML Source'} mode. The rich text editor may be slow with content this large.
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Newsletter Content *
              </label>

              {editorMode === 'rich' && (
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                  placeholder="Write your newsletter content here..."
                />
              )}

              {editorMode === 'html' && (
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  style={{ height: '550px', resize: 'vertical' }}
                  placeholder="Paste or edit HTML content here..."
                />
              )}

              {editorMode === 'preview' && (
                <div
                  className="border border-gray-300 rounded-lg overflow-auto bg-white"
                  style={{ height: '550px' }}
                >
                  {formData.content ? (
                    <div
                      className="p-4"
                      dangerouslySetInnerHTML={{ __html: formData.content }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      No content to preview
                    </div>
                  )}
                </div>
              )}

              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content}</p>
              )}
            </div>
          </div>

          {/* Images Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Images</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Images
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">Select multiple images to add to newsletter (JPG, PNG, GIF)</p>

              {/* Image Preview */}
              {formData.images && formData.images.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Images</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Selected ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Options */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Publishing Options</h2>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="featured"
                  id="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#135879] focus:ring-[#135879] border-gray-300 rounded"
                />
                <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                  Feature this newsletter
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Read Time
                </label>
                <input
                  type="text"
                  name="read_time"
                  value={formData.read_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#135879]"
                  placeholder="e.g., 5 min read"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
      </>
      )}
    </div>
  );
};

export default BlogEditor;
